import { useContext, useEffect, useRef, useState } from 'react';

import { RiskContext } from '../../../../../providers/portfolio-performance/risk';
import { MetricKey } from '../../../../../providers/portfolio-performance/risk/risk.types';
import { COMBINED_USD_TOKEN } from '../../../../../providers/portfolio-performance/risk/risk.constants';
import {
  formatTokenName,
  getBaseSymbol,
} from '../../../../../providers/portfolio-performance/risk/risk.helpers';
import Utils from '../../../../../utils/immersive_utils';
import './styles.scss';

const METRIC_ORDER: MetricKey[] = ['PV', 'DELTA', 'THETA', 'GAMMA', 'VEGA'];
const METRIC_LABELS: Record<MetricKey, string> = {
  PV: 'PV',
  DELTA: 'Delta',
  THETA: 'Theta',
  GAMMA: 'Gamma',
  VEGA: 'Vega',
};
// Dollar-value Greeks (PV/Theta/Vega) follow the account's own reporting
// currency — USD for a QUOTE-denominated account, the base token
// otherwise. Delta/Gamma stay in base-token terms regardless of
// riskTokenType (per-unit-of-underlying is the meaningful frame for both,
// even on a USD-denominated account).
const DOLLAR_VALUE_METRICS = new Set<MetricKey>(['PV', 'THETA', 'VEGA']);

const formatTimeAgo = (timestamp: number | null, nowTick: number): string => {
  if (timestamp === null) return '-';
  const seconds = Math.max(0, Math.floor(nowTick - timestamp));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
};

const MetricTile = ({
  label,
  value,
  flashDirection,
}: {
  label: string;
  value: string;
  flashDirection: 'up' | 'down' | null;
}) => (
  <div className={`live-risk-metric-tile${flashDirection ? ` flash-${flashDirection}` : ''}`}>
    <span className="live-risk-metric-label">{label}</span>
    <span className="live-risk-metric-value">{value}</span>
  </div>
);

export const LiveRiskMetricsBand = () => {
  const { subscribeToPerformanceTick, riskTokenType, selectedToken } = useContext(RiskContext);

  const [latest, setLatest] = useState<Record<MetricKey, number> | null>(null);
  const [lastTickAt, setLastTickAt] = useState<number | null>(null);
  const previousRef = useRef<Record<MetricKey, number> | null>(null);
  const [flashDirections, setFlashDirections] = useState<Partial<Record<MetricKey, 'up' | 'down'>>>(
    {},
  );
  const flashTimeoutRef = useRef<number | null>(null);

  // The combined view is a pure USD total summed across the settlement books,
  // so every tile is USD regardless of the BASE/QUOTE toggle.
  const isCombined = selectedToken === COMBINED_USD_TOKEN;
  const effectiveType = isCombined ? 'QUOTE' : riskTokenType;

  // riskTokenType can change without resubscribing, so the tick handler
  // (created once, below) reads precision via a ref rather than a stale
  // closure value.
  const precision = effectiveType === 'BASE' ? 4 : 2;
  const precisionRef = useRef(precision);
  precisionRef.current = precision;

  const baseSymbol = getBaseSymbol(selectedToken) || 'BTC';
  const getUnit = (key: MetricKey): string => {
    if (isCombined) return 'USD';
    return effectiveType === 'QUOTE' && DOLLAR_VALUE_METRICS.has(key) ? 'USD' : baseSymbol;
  };

  useEffect(() => {
    const unsubscribe = subscribeToPerformanceTick(({ timestamp, metrics }) => {
      const previous = previousRef.current;
      previousRef.current = metrics;
      setLatest(metrics);
      setLastTickAt(timestamp);

      if (!previous) return;

      const directions: Partial<Record<MetricKey, 'up' | 'down'>> = {};
      METRIC_ORDER.forEach((key) => {
        const prevVal = previous[key];
        const nextVal = metrics[key];
        if (nextVal === undefined || prevVal === undefined) return;
        // Compare the rounded display string, not the raw float — a change
        // too small to show at this precision shouldn't trigger a flash.
        if (
          Utils.formatNumber(prevVal, precisionRef.current) ===
          Utils.formatNumber(nextVal, precisionRef.current)
        ) {
          return;
        }
        directions[key] = nextVal > prevVal ? 'up' : 'down';
      });
      if (Object.keys(directions).length === 0) return;

      setFlashDirections(directions);
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = window.setTimeout(() => setFlashDirections({}), 900);
    });

    return unsubscribe;
  }, [subscribeToPerformanceTick]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // Ticks every second so "time since last update" stays live without new data.
  const [nowTick, setNowTick] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now() / 1000), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="live-risk-metrics-band">
      {METRIC_ORDER.map((key) => (
        <MetricTile
          key={key}
          label={`${METRIC_LABELS[key]} (${getUnit(key)})`}
          value={latest ? Utils.formatNumber(latest[key], precision) : '-'}
          flashDirection={flashDirections[key] ?? null}
        />
      ))}
      <div className="live-risk-metric-tile">
        <span className="live-risk-metric-label">Token</span>
        <span className="live-risk-metric-value">{formatTokenName(selectedToken) || '-'}</span>
      </div>
      <div className="live-risk-metric-tile live-risk-metric-tile-live">
        <span className="live-risk-metric-label">Live</span>
        <span className="live-risk-metric-value">
          <span className="live-dot" />
          {formatTimeAgo(lastTickAt, nowTick)}
        </span>
      </div>
    </div>
  );
};
